import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CanvasNode } from "@opus-dt/shared-types";
import { labelForType } from "./helpers";
import { MachineForm } from "./forms/MachineForm";
import { SensorScaleForm } from "./forms/SensorScaleForm";
import { EquipmentForm, CheckpointForm, HumanStepForm } from "./forms/SimpleForms";

interface Props {
  node: CanvasNode;
  lineId: string;
  onChange: (next: Partial<CanvasNode>) => void;
  onDelete: () => void;
  onClose: () => void;
  onOpenSidebar?: (node: CanvasNode, options?: { canvasAdd?: boolean }) => void;
  usedMachineIds: Set<string>;
}

export function NodePropertiesPanel({
  node,
  lineId,
  onChange,
  onDelete,
  onClose,
  onOpenSidebar,
  usedMachineIds,
}: Props) {
  void lineId;
  void usedMachineIds;
  const isImmutable = node.type === "START" || node.type === "END";

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-label="Propriedades do nó"
      className="fixed right-0 top-0 z-30 flex h-full w-full max-w-sm flex-col border-l bg-card shadow-2xl"
    >
      <header className="flex items-start justify-between gap-2 border-b p-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {labelForType(node.type)}
          </p>
          <h3 className="text-base font-semibold">{node.label}</h3>
        </div>
        <Button variant="ghost" size="icon" aria-label="Fechar" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="space-y-4 overflow-y-auto p-4">
        <div className="space-y-1.5">
          <Label htmlFor="node-label">Rótulo</Label>
          <Input
            id="node-label"
            value={node.label}
            onChange={(e) => onChange({ label: e.target.value })}
            maxLength={120}
          />
        </div>

        {node.type === "MACHINE" ? <MachineForm node={node} onOpenSidebar={onOpenSidebar} /> : null}
        {node.type === "SENSOR" || node.type === "SCALE" ? (
          <SensorScaleForm node={node} onChange={onChange} />
        ) : null}
        {node.type === "EQUIPMENT" ? <EquipmentForm node={node} onChange={onChange} /> : null}
        {node.type === "CONTROL_POINT" || node.type === "INSPECTION" ? (
          <CheckpointForm node={node} onChange={onChange} />
        ) : null}
        {node.type === "HUMAN_STEP" ? <HumanStepForm node={node} onChange={onChange} /> : null}

        <div className="space-y-1.5">
          <Label>Posição</Label>
          <p className="font-mono text-xs text-muted-foreground">
            x: {Math.round(node.x)} · y: {Math.round(node.y)}
          </p>
        </div>
      </div>

      <footer className="border-t p-4">
        {isImmutable ? (
          <p className="text-center text-xs text-muted-foreground">
            Nós de início e fim não podem ser removidos.
          </p>
        ) : (
          <Button
            variant="destructive"
            className="w-full"
            onClick={onDelete}
            aria-label="Remover nó"
          >
            Remover nó
          </Button>
        )}
      </footer>
    </aside>
  );
}
